import React from 'react';
import { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import './classes/ConferenceRegistration.css';

import { configData } from "../config";

// The sign-up popup behind the "Be The First To Know" button on conference pages.
// Same fields, checks and wording as the Drupal popup block; it posts to the
// existing smwwdrupal/bethefirsttoknow endpoint.

// Values are sent as tags exactly as the Drupal form sent them
const CONFERENCES = [
    { value: "Baseball Career Conference", label: "Baseball Career Conference" },
    { value: "Basketball Career Conference", label: "Basketball Career Conference" },
    { value: "NFL Combine Football Career Conference", label: "NFL Combine Football Career Conference" },
    { value: "NHL Draft Hockey Career Conference", label: "NHL Draft Hockey Career Conference" },
    { value: " Race Track Business Conference ", label: "Race Track Business Conference" },
    { value: "Soccer Career Conference at the NWSL Draft", label: "Soccer Career Conference at the NWSL Draft" },
    { value: "Toronto PrimeTime Sports Conference", label: "Toronto PrimeTime Sports Conference" },
];

// Addresses the Drupal form refuses
const REFUSED_EMAILS = ['regan.jim65@gmail.com', 'jamesfregan68@gmail.com', 'lisaostrom90@gmail.com'];

const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const BeTheFirstToKnowPopup = ({ open, onClose }) => {
    const app_url = configData.APP_URL;
    const sitekey = configData.GOOGLE_RECAPTCHA_SITE_KEY;
    const recaptchaRef = useRef(null);
    const emailRef = useRef(null);

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [tags, setTags] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Start from an empty form each time the popup opens
    useEffect(() => {
        if (open) {
            setEmail('');
            setFirstName('');
            setLastName('');
            setTags([]);
            setIsSubmitting(false);
            setIsSubmitted(false);
        }
    }, [open]);

    // The live dialog puts the cursor in the email field when it opens
    useEffect(() => {
        if (open && !isSubmitted) emailRef.current?.focus();
    }, [open, isSubmitted]);

    useEffect(() => {
        if (!open) return;

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [open, onClose]);

    if (!open) return null;

    const toggleTag = (value) => {
        setTags((current) => current.includes(value) ? current.filter((tag) => tag !== value) : [...current, value]);
    };

    const validate = (recaptchaValue) => {
        const lowerEmail = email.toLowerCase();

        if (email === '') return 'Please enter your email';
        if (lowerEmail === 'info@smww.com') return 'Please enter your email address (cannot be info@smww.com)';
        if (REFUSED_EMAILS.includes(lowerEmail)) return 'Please enter your email address.';
        if (!EMAIL_PATTERN.test(email)) return 'Invalid email address';
        if (firstName.trim() === '') return 'Please enter your first name';
        if (lastName.trim() === '') return 'Please enter your last name';
        if (tags.length === 0) return 'Please select at least one conference.';
        if (!recaptchaValue) return "Please verify you're not a robot.";

        return '';
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;

        // The live form reports each problem in an alert box
        const recaptchaValue = recaptchaRef.current ? recaptchaRef.current.getValue() : '';
        const validationError = validate(recaptchaValue);
        if (validationError) {
            window.alert(validationError);
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(app_url + 'smwwdrupal/bethefirsttoknow', {
                method: 'POST',
                body: JSON.stringify({
                    email: email,
                    custom_fields: {
                        source: 'conference',
                        first_name: firstName,
                        last_name: lastName,
                    },
                    tags: tags,
                    pageURL: window.location.href,
                    'g-recaptcha-response': recaptchaValue,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (data?.data?.result === "user_created") {
                setIsSubmitted(true);
            } else {
                window.alert('Sorry, something went wrong. Please try again.');
            }
        } catch (err) {
            window.alert('Sorry, something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Live's dialog: 650px wide, 95% of the window on a phone (its script checks the screen
    // width); after sending, a 300px "Success!" dialog in its place
    const isPhone = window.screen.width < 767;
    const dialogStyle = !isSubmitted && isPhone ? { width: '95%' } : undefined;

    return (
        <div className="btftk-overlay" onClick={onClose}>
            <div className={'btftk-dialog' + (isSubmitted ? ' btftk-dialog--success' : '')} style={dialogStyle} role="dialog" aria-modal="true" aria-labelledby="btftk-title" onClick={(event) => event.stopPropagation()}>
                <div className="btftk-titlebar">
                    <span className="btftk-title" id="btftk-title">{isSubmitted ? 'Success!' : 'Be The First To Know When The Next Conference Date Is Announced'}</span>
                    <button type="button" className="btftk-close" aria-label="Close" onClick={onClose}><span /></button>
                </div>

                {isSubmitted ? (
                    <div className="btftk-content">
                        <div className="btftk-success">
                            <p>Thank you! We will let you know as soon as conference dates are announced!</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="btftk-content">
                            <form className="btftk-form" id="btftk-form" onSubmit={handleSubmit} noValidate>
                                <div className="btftk-group">
                                    <label htmlFor="btftk-email"><span className="btftk-required">*</span> Email address</label>
                                    <input className="btftk-input" id="btftk-email" name="email" type="text" ref={emailRef} value={email} onChange={(event) => setEmail(event.target.value)} />
                                </div>
                                <div className="btftk-group">
                                    <label htmlFor="btftk-firstname"><span className="btftk-required">*</span> First Name</label>
                                    <input className="btftk-input" id="btftk-firstname" name="first_name" type="text" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                                </div>
                                <div className="btftk-group">
                                    <label htmlFor="btftk-lastname"><span className="btftk-required">*</span> Last Name</label>
                                    <input className="btftk-input" id="btftk-lastname" name="last_name" type="text" value={lastName} onChange={(event) => setLastName(event.target.value)} />
                                </div>
                                <div className="btftk-group">
                                    <label>Conference</label>
                                    {CONFERENCES.map((conference) => (
                                        <div className="btftk-checkbox" key={conference.value}>
                                            <label className="btftk-check">
                                                <input name="tags[]" type="checkbox" value={conference.value} checked={tags.includes(conference.value)} onChange={() => toggleTag(conference.value)} />
                                                {' '}{conference.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <div className="btftk-group btftk-recaptcha">
                                    <ReCAPTCHA ref={recaptchaRef} sitekey={sitekey} />
                                </div>
                            </form>
                        </div>
                        <div className="btftk-buttonpane">
                            <div className="btftk-buttonset">
                                <button type="submit" form="btftk-form" className="btftk-submit" disabled={isSubmitting}>
                                    <span>I Want To Be The First To Know When Conference Dates Are Announced!</span>
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {isSubmitting && (
                    <div className="btftk-loading">
                        <img src={`${process.env.PUBLIC_URL}/images/ajax-loader.gif`} alt="" />
                    </div>
                )}
            </div>
        </div>
    );
};
export default BeTheFirstToKnowPopup;
